"use client"
import {
  Button,
  Card,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Tooltip,
  useDisclosure,
  useToast,
  Box,
  Skeleton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  InputGroup,
  InputLeftElement,
  Spinner,
} from "@chakra-ui/react"
import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  addJobPreferenceAsync,
  fetchJobPreferencesAsync,
  selectJobPreference,
  updateJobPreferenceAsync,
} from "./JobPreferenceSlice"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faRupeeSign } from "@fortawesome/free-solid-svg-icons"
import { useForm } from "react-hook-form"
import {
  fetchJobDepartmentsAsync,
  selectJobDepartments,
} from "../../../AdminPanel/JobPostingSlice/JobDepartment/JobDepartmentSlice"
import SpinnerLoad from "../../../common/Spinner"
import {
  fetchPublicCoreLocationsAsync,
  selectPublicCoreLocations,
} from "../../../jobSearch/FetchLocationSlice/FetchLocationSlice"
import { fetchJobSkillsAsync, selectJobSkills } from "../../../AdminPanel/JobPostingSlice/JobSkillsList/JobSkillsSlice"
import EmptyState from "../../components/Tester"
import { FaBriefcase } from "react-icons/fa"

function JobPreferences2() {
  const dispatch = useDispatch()
  const toast = useToast()

  // Store data
  const jobPreferences = useSelector(selectJobPreference)
  const jobDepartments = useSelector(selectJobDepartments)
  const coreLocation = useSelector(selectPublicCoreLocations)
  const coreSkills = useSelector(selectJobSkills)
  const [locationSearch, setLocationSearch] = useState("")
  const coreLocationsLoading = useSelector((state) => state.publicCoreLocations?.loading)

  // 1-time fetch refs
  const hasFetchedPreferences = useRef(false)
  const hasFetchedDepartments = useRef(false)
  const hasFetchedLocations = useRef(false)
  const hasFetchedSkills = useRef(false)

  // Local state
  const [loading, setLoading] = useState(false)
  const [JobPreference, setJobPreference] = useState(null)
  const [isUpdate, setIsUpdate] = useState(false)
  const [location, setLocation] = useState([])
  const [locationId, setLocationId] = useState([])
  const [skills, setSkills] = useState([])
  const [skillsId, setSkillsId] = useState([])

  // Important: start as false so revisit doesn't flash skeleton
  const [isInitialLoading, setIsInitialLoading] = useState(false)

  const {
    isOpen: isJobPreferenceModalOpen,
    onOpen: onJobPreferenceModalOpen,
    onClose: onJobPreferenceModalClose,
  } = useDisclosure()

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onChange",
  })

  const hasJobPrefs = Array.isArray(jobPreferences) && jobPreferences.length > 0
  const hasDepts = Array.isArray(jobDepartments) && jobDepartments.length > 0
  const hasLocs = Array.isArray(coreLocation) && coreLocation.length > 0
  const hasSkills = Array.isArray(coreSkills) && coreSkills.length > 0

  // Fetch all needed lists and show skeleton only when job preferences need loading
  useEffect(() => {
    const fetchAll = async () => {
      const promises = []

      if (!hasFetchedPreferences.current && !hasJobPrefs) {
        hasFetchedPreferences.current = true
        promises.push(dispatch(fetchJobPreferencesAsync()))
      }
      if (!hasFetchedDepartments.current && !hasDepts) {
        hasFetchedDepartments.current = true
        promises.push(dispatch(fetchJobDepartmentsAsync()))
      }
      if (!hasFetchedLocations.current && !hasLocs) {
        hasFetchedLocations.current = true
        promises.push(dispatch(fetchPublicCoreLocationsAsync()))
      }
      if (!hasFetchedSkills.current && !hasSkills) {
        hasFetchedSkills.current = true
        promises.push(dispatch(fetchJobSkillsAsync()))
      }

      if (promises.length > 0) {
        // Only show skeleton if we are actually missing job preferences
        if (!hasJobPrefs) setIsInitialLoading(true)
        try {
          await Promise.all(promises)
        } catch (e) {
          // ignore; render will handle empty/error state
        } finally {
          setIsInitialLoading(false)
        }
      } else {
        // Nothing to fetch; ensure we are not stuck in skeleton
        setIsInitialLoading(false)
      }
    }
    fetchAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, hasJobPrefs, hasDepts, hasLocs, hasSkills])

  // When the modal opens, load initial list without keyword
  useEffect(() => {
    if (isJobPreferenceModalOpen) {
      dispatch(fetchPublicCoreLocationsAsync({ keyword: "" }))
    }
  }, [isJobPreferenceModalOpen, dispatch])

  // Debounce on search term inside modal
  useEffect(() => {
    if (!isJobPreferenceModalOpen) return
    const t = setTimeout(() => {
      dispatch(fetchPublicCoreLocationsAsync({ keyword: locationSearch.trim() }))
    }, 300)
    return () => clearTimeout(t)
  }, [locationSearch, isJobPreferenceModalOpen, dispatch])

  // Reuse handler by selecting from Menu
  const selectLocationByUuid = (uuid) => {
    handleSelectedLocation({ target: { value: uuid } })
  }

  // Handlers: location/skills multi-select with chips
  const handleSelectedLocation = (e) => {
    const selectedLocationId = e.target.value
    if (!selectedLocationId) return

    const selectedLocation = coreLocation.find((item) => item.uuid === selectedLocationId)
    if (selectedLocation && !location.some((loc) => loc.uuid === selectedLocation.uuid)) {
      const updatedLocation = [...location, selectedLocation]
      const updatedLocationId = [...locationId, selectedLocation.uuid]
      setLocation(updatedLocation)
      setLocationId(updatedLocationId)
      setValue("preferred_location", updatedLocationId)
    }
    e.target.value = ""
  }

  const removeLocation = (index) => {
    const updatedLocation = location.filter((_, i) => i !== index)
    const updatedLocationId = updatedLocation.map((loc) => loc.uuid)
    setLocation(updatedLocation)
    setLocationId(updatedLocationId)
    setValue("preferred_location", updatedLocationId)
  }

  const handleSelectedSkills = (e) => {
    const selectedSkillUuid = e.target.value
    if (!selectedSkillUuid) return
    const selectedSkill = coreSkills?.find((item) => item.uuid === selectedSkillUuid)
    if (selectedSkill && !skills.some((skill) => skill.uuid === selectedSkill.uuid)) {
      const updatedSkills = [...skills, selectedSkill]
      const updatedSkillIds = updatedSkills.map((skill) => skill.uuid)
      setSkills(updatedSkills)
      setSkillsId(updatedSkillIds)
      setValue("preferred_skills", updatedSkillIds)
    }
    e.target.value = ""
  }

  const removeSkill = (index) => {
    const updatedSkills = skills.filter((_, i) => i !== index)
    const updatedSkillIds = updatedSkills.map((skill) => skill.uuid)
    setSkills(updatedSkills)
    setSkillsId(updatedSkillIds)
    setValue("preferred_skills", updatedSkillIds)
  }

  const openAddModal = () => {
    resetFormAndStates()
    setIsUpdate(false)
    onJobPreferenceModalOpen()
  }

  const openUpdateModal = (jobPreference) => {
    if (jobPreference) {
      setJobPreference(jobPreference)
      setIsUpdate(true)

      // Reset form then set values
      reset()

      setValue("job_type", jobPreference.job_type || "")
      setValue("employment_type", jobPreference.employment_type || "")
      setValue("preferred_shift", jobPreference.preferred_shift || "")
      setValue("expected_salary_currency", jobPreference.expected_salary_currency || "INR")
      setValue("expected_salary_amount", jobPreference.expected_salary_amount || "")
      setValue("notice_period", jobPreference.notice_period || "")
      setValue("pay_type", jobPreference.pay_type || "")
      setValue("department_uuid", jobPreference.department?.uuid || "")

      // Locations
      if (Array.isArray(jobPreference.preference_location) && jobPreference.preference_location.length > 0) {
        const locations = jobPreference.preference_location
          .map((loc) => loc.location)
          .filter((location) => location && location.uuid && location.title)
        const locationIds = locations.map((loc) => loc.uuid)
        setLocation(locations)
        setLocationId(locationIds)
        setValue("preferred_location", locationIds)
      } else {
        setLocation([])
        setLocationId([])
        setValue("preferred_location", [])
      }

      // Skills
      if (Array.isArray(jobPreference.preference_skills) && jobPreference.preference_skills.length > 0) {
        const skillObjs = jobPreference.preference_skills
          .map((skill) => skill.skill)
          .filter((s) => s && s.uuid && s.title)
        const skillIds = skillObjs.map((s) => s.uuid)
        setSkills(skillObjs)
        setSkillsId(skillIds)
        setValue("preferred_skills", skillIds)
      } else {
        setSkills([])
        setSkillsId([])
        setValue("preferred_skills", [])
      }

      onJobPreferenceModalOpen()
    }
  }

  const handleSave = async (newJobPreference) => {
    setLoading(true)
    try {
      const jobPreferenceData = {
        ...newJobPreference,
        preferred_location: locationId.length > 0 ? locationId : [],
        preferred_skills: skillsId.length > 0 ? skillsId : [],
      }
      await dispatch(addJobPreferenceAsync({ newJobPreference: jobPreferenceData }))
      await dispatch(fetchJobPreferencesAsync())
      toast({
        title: "Success",
        description: "Job preference added successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      })
      onJobPreferenceModalClose()
      resetFormAndStates()
    } catch (error) {
      console.error("Failed to add job", error)
      toast({
        title: "Error",
        description: "Failed to add job preference. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleUpdate = async (jobPreferenceData) => {
    setLoading(true)

    const finalLocationIds = locationId.length > 0 ? locationId : []
    const finalSkillIds = skillsId.length > 0 ? skillsId : []

    const newData = {
      preferred_location: finalLocationIds,
      preferred_skills: finalSkillIds,
      department_uuid: jobPreferenceData.department_uuid,
      job_type: jobPreferenceData.job_type,
      employment_type: jobPreferenceData.employment_type,
      preferred_shift: jobPreferenceData.preferred_shift,
      expected_salary_currency: jobPreferenceData.expected_salary_currency,
      expected_salary_amount: jobPreferenceData.expected_salary_amount,
      pay_type: jobPreferenceData.pay_type,
      notice_period: jobPreferenceData.notice_period,
    }

    if (JobPreference) {
      try {
        await dispatch(
          updateJobPreferenceAsync({
            uuid: JobPreference.uuid,
            jobPreferenceData: newData,
          }),
        )
        toast({
          title: "Success",
          description: "Job preference updated successfully!",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        })
        await dispatch(fetchJobPreferencesAsync())
        onJobPreferenceModalClose()
        resetFormAndStates()
      } catch (error) {
        console.error("Failed to update data", error)
        toast({
          title: "Error",
          description: "Failed to update job preference. Please try again.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        })
      } finally {
        setLoading(false)
      }
    } else {
      console.warn("JobPreference is not defined")
      toast({
        title: "Error",
        description: "Job preference data not found.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      })
      setLoading(false)
    }
  }

  const handleFormSubmit = (data) => {
    if (isUpdate) {
      handleUpdate(data)
    } else {
      handleSave(data)
    }
  }

  const resetFormAndStates = () => {
    reset()
    setLocation([])
    setLocationId([])
    setSkills([])
    setSkillsId([])
    setJobPreference(null)
    setLocationSearch("") // reset search
  }

  const handleModalClose = () => {
    resetFormAndStates()
    onJobPreferenceModalClose()
  }

  // Simple validation helpers (UI only)
  const salaryAmountRegister = register("expected_salary_amount", {
    required: "Expected Salary is required",
    min: { value: 1, message: "Amount must be at least 1" },
    valueAsNumber: true,
    validate: (v) => (Number.isFinite(v) ? true : "Enter a valid number"),
  })

  const showSkeleton = isInitialLoading && !hasJobPrefs

  return (
    <div className="" style={{ fontFamily: "Inter, sans-serif" }}>
      <div className="bg-mainBg">
        {showSkeleton ? (
          <div className="bg-white rounded-xl w-full px-3 sm:px-5 py-4 shadow-sm  border-gray-100">
            <div className="flex sm:flex-row sm:items-center justify-between gap-2 mb-4">
              <Skeleton height="28px" width="200px" />
              <Skeleton height="36px" width="90px" />
            </div>
            <div className="w-full mx-auto border rounded-lg overflow-hidden shadow-sm">
              <div className="p-4 space-y-3">
                <Skeleton height="16px" width="40%" />
                <Skeleton height="16px" width="30%" />
                <Skeleton height="16px" width="50%" />
                <Skeleton height="16px" width="45%" />
              </div>
            </div>
          </div>
        ) : jobPreferences && jobPreferences.length > 0 ? (
          jobPreferences?.map((jobPreference) => (
            <div key={jobPreference.id}>
              <div className="bg-white rounded-xl w-full px-3 sm:px-5 py-4 shadow-sm  border-gray-100">
                <div className="flex sm:flex-row sm:items-center justify-between gap-3 mb-3">
                  <div className="flex items-start gap-2">
                    <div className="h-9 w-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                      <FaBriefcase />
                    </div>
                    <div>
                      <h3 className="text-xl sm:text-2xl md:text-3xl text-left font-semibold">Job Preferences</h3>
                      <p className="text-xs text-gray-500">Update your Job Preferences</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Tooltip label="Edit Job Preference">
                      <Button colorScheme="blue" onClick={() => openUpdateModal(jobPreference)} className="shadow-sm">
                        <i className="fa-sharp fa-solid fa-pen"></i>
                      </Button>
                    </Tooltip>
                  </div>
                </div>

                <div className="group relative border rounded-xl shadow-sm hover:shadow-md transition-all duration-200 bg-white overflow-hidden">
                  {/* Accent bar */}
                  <div className="absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-blue-500 to-indigo-500" />
                  <div className="p-4">
                    <div className="flex flex-wrap">
                      <div className="w-full md:w-1/2 md:mb-0 pr-0 md:pr-4">
                        <div className="text-start mb-4">
                          <h6 className="text-gray-500 font-medium">Employment Type</h6>
                          <h6 className="text-blue-600 font-semibold">{jobPreference.employment_type}</h6>
                        </div>
                        <div className="text-start mb-4">
                          <h6 className="text-gray-500 font-medium">Preferred Shift</h6>
                          <h6 className="text-gray-800 font-medium">{jobPreference.preferred_shift}</h6>
                        </div>
                        <div className="text-start mb-4">
                          <h6 className="text-gray-500 font-semibold">Skills</h6>
                          <h6 className="text-gray-800 font-medium">
                            {jobPreference.preference_skills?.map((skill) => skill.skill?.title).join(", ")}
                          </h6>
                        </div>
                      </div>
                      <div className="w-full md:w-1/2 pl-0 md:pl-4">
                        <div className="text-start mb-4">
                          <h6 className="text-gray-500 font-medium">Expected Salary</h6>
                          <h6 className="text-blue-600 font-semibold">
                            <FontAwesomeIcon size="sm" icon={faRupeeSign} /> {jobPreference.expected_salary_amount}{" "}
                            {jobPreference.pay_type}
                          </h6>
                        </div>
                        <div className="text-start mb-4">
                          <h6 className="text-gray-500 font-medium">Preferred Location</h6>
                          <h6 className="text-gray-800 font-medium">
                            {jobPreference.preference_location?.map((loc) => loc.location?.title).join(", ")}
                          </h6>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="bg-white flex flex-col" style={{ fontFamily: "Inter, sans-serif" }}>
            <Card padding={"2rem"}>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <div className="h-9 w-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                    <FaBriefcase />
                  </div>
                  <h1 className="text-2xl md:text-3xl font-semibold">Job Preferences</h1>
                </div>
                <div className="py-3 flex gap-3">
                  <Button onClick={openAddModal} colorScheme="blue" className="shadow">
                    Add<i className="fa-solid fa-plus px-2"></i>
                  </Button>
                </div>
              </div>
              <EmptyState
                title="Job Preferences"
                icon={FaBriefcase}
                emptyMessage="Start by adding your Job Preferences"
                addButtonText="Add Job Preferences"
                onAdd={openAddModal}
              />
            </Card>
          </div>
        )}
      </div>

      {/* Single Job Preference Modal for Add/Update */}
      <Modal
        isOpen={isJobPreferenceModalOpen}
        onClose={handleModalClose}
        isCentered
        closeOnOverlayClick={!loading}
      >
        <ModalOverlay />
        <ModalContent maxW="5xl">
          {/* Prevent native tooltip validation */}
          <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
            <ModalHeader>{isUpdate ? "Edit Job Preferences" : "Job Preference"}</ModalHeader>
            <ModalCloseButton isDisabled={loading} />
            <ModalBody pb={6}>
              {/* Locations */}
              <FormControl>
                <FormLabel>Preferred Location</FormLabel>

                <Menu closeOnSelect={false} matchWidth>
                  <MenuButton
                    as={Button}
                    variant="outline"
                    w="100%"
                    rightIcon={<i className="fa-solid fa-chevron-down"></i>}
                    className="text-left font-normal"
                  >
                    Select a location
                  </MenuButton>

                  <MenuList maxH="320px" overflowY="auto" minW="sm">
                    <Box p={3} borderBottom="1px solid" borderColor="gray.100">
                      <InputGroup>
                        <InputLeftElement pointerEvents="none">
                          <i className="fa-solid fa-magnifying-glass text-gray-400"></i>
                        </InputLeftElement>
                        <Input
                          placeholder="Search city..."
                          value={locationSearch}
                          onChange={(e) => setLocationSearch(e.target.value)}
                          autoFocus
                        />
                      </InputGroup>
                    </Box>

                    {coreLocationsLoading ? (
                      <Box p={3} textAlign="center" display="flex" alignItems="center" justifyContent="center" gap={2}>
                        <Spinner size="sm" /> Loading...
                      </Box>
                    ) : Array.isArray(coreLocation) && coreLocation.length > 0 ? (
                      coreLocation.map((item) => (
                        <MenuItem key={item.uuid} onClick={() => selectLocationByUuid(item.uuid)}>
                          {item.title}
                        </MenuItem>
                      ))
                    ) : (
                      <Box p={3} color="gray.500">
                        No results
                      </Box>
                    )}
                  </MenuList>
                </Menu>

                {location && location.length > 0 && (
                  <div className="mt-3 w-fit flex flex-wrap gap-2">
                    {location?.map((loc, index) => (
                      <div key={index} className="bg-blue-600 p-1.5 px-2 text-white rounded text-sm">
                        {loc.title}
                        <button
                          type="button"
                          className="ml-2 text-white/90 hover:text-white"
                          onClick={() => removeLocation(index)}
                          aria-label={`Remove ${loc.title}`}
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </FormControl>

              {/* Skills */}
              <FormControl mt={4}>
                <FormLabel>Preferred Skills</FormLabel>
                <Select placeholder="Select a skill" onChange={handleSelectedSkills}>
                  {coreSkills &&
                    coreSkills?.map((item) => (
                      <option key={item.uuid} value={item?.uuid}>
                        {item?.title}
                      </option>
                    ))}
                </Select>
                {skills && skills.length > 0 && (
                  <div className="mt-3 w-fit flex flex-wrap gap-2">
                    {skills?.map((skill, index) => (
                      <div key={index} className="bg-indigo-600 p-1.5 px-2 text-white rounded text-sm">
                        {skill?.title}
                        <button
                          type="button"
                          className="ml-2 text-white/90 hover:text-white"
                          onClick={() => removeSkill(index)}
                          aria-label={`Remove ${skill?.title}`}
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </FormControl>

              <FormControl mt={4} isRequired isInvalid={!!errors.job_type}>
                <FormLabel>Job Type</FormLabel>
                <Select
                  {...register("job_type", {
                    required: "Job Type is required",
                  })}
                  placeholder="Select Job Type"
                >
                  <option value="Permanent">Permanent</option>
                  <option value="Fixed-Term">Fixed-Term</option>
                  <option value="Commission-Based">Commission-Based</option>
                  <option value="Volunteer">Volunteer</option>
                  <option value="Intern">Intern</option>
                </Select>
                <FormErrorMessage>{errors.job_type?.message}</FormErrorMessage>
              </FormControl>

              <FormControl mt={4} isRequired isInvalid={!!errors.employment_type}>
                <FormLabel>Employment Type</FormLabel>
                <Select
                  placeholder="Select option"
                  {...register("employment_type", {
                    required: "Employment Type is required",
                  })}
                >
                  <option value="Full Time">Full Time</option>
                  <option value="Part Time">Part Time</option>
                </Select>
                <FormErrorMessage>{errors.employment_type?.message}</FormErrorMessage>
              </FormControl>

              <FormControl mt={4} isRequired isInvalid={!!errors.preferred_shift}>
                <FormLabel>Preferred Shift</FormLabel>
                <Select
                  placeholder="Select option"
                  {...register("preferred_shift", {
                    required: "Preferred Shift is required",
                  })}
                >
                  <option value="Day Shift">Day Shift</option>
                  <option value="Night Shift">Night Shift</option>
                  <option value="Flexible">Flexible</option>
                </Select>
                <FormErrorMessage>{errors.preferred_shift?.message}</FormErrorMessage>
              </FormControl>

              <FormControl mt={4} isRequired isInvalid={!!errors.expected_salary_currency}>
                <FormLabel>Expected Salary Currency</FormLabel>
                <Select
                  {...register("expected_salary_currency", {
                    required: "Currency is required",
                  })}
                  defaultValue="INR"
                  placeholder="Select currency"
                >
                  <option value="INR">INR</option>
                  <option value="USD">USD</option>
                </Select>
                <FormErrorMessage>{errors.expected_salary_currency?.message}</FormErrorMessage>
              </FormControl>

              <div className="flex gap-2 mt-4">
                <FormControl isRequired isInvalid={!!errors.expected_salary_amount}>
                  <FormLabel>Expected Salary</FormLabel>
                  <Input
                    type="number"
                    placeholder="Expected Salary"
                    {...salaryAmountRegister}
                  />
                  <FormErrorMessage>{errors.expected_salary_amount?.message}</FormErrorMessage>
                </FormControl>
                <FormControl isRequired isInvalid={!!errors.pay_type}>
                  <FormLabel>Pay Type</FormLabel>
                  <Select
                    {...register("pay_type", {
                      required: "Pay Type is required",
                    })}
                    placeholder="Select Pay Type"
                  >
                    <option value="Per Month">Per Month</option>
                    <option value="Per Year">Per Year</option>
                  </Select>
                  <FormErrorMessage>{errors.pay_type?.message}</FormErrorMessage>
                </FormControl>
              </div>

              <FormControl mt={4} isRequired isInvalid={!!errors.notice_period}>
                <FormLabel>Notice Period</FormLabel>
                <Select
                  placeholder="Notice Period"
                  {...register("notice_period", {
                    required: "Notice Period is required",
                  })}
                >
                  <option value="Immediate Join">Immediate Join</option>
                  <option value="15 Days or less">15 Days or less</option>
                  <option value="1 month">1 month</option>
                  <option value="2 months">2 months</option>
                  <option value="3 months">3 months</option>
                  <option value="More than 3 months">More than 3 months</option>
                  <option value="Serving Notice Period">Serving Notice Period</option>
                </Select>
                <FormErrorMessage>{errors.notice_period?.message}</FormErrorMessage>
              </FormControl>

              <FormControl mt={4} isRequired isInvalid={!!errors.department_uuid}>
                <FormLabel>Job Department</FormLabel>
                <Select
                  placeholder="Select option"
                  {...register("department_uuid", {
                    required: "Job Department is required",
                  })}
                >
                  {jobDepartments?.map((job) => (
                    <option key={job.id} value={job.uuid}>
                      {job.title}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage>{errors.department_uuid?.message}</FormErrorMessage>
              </FormControl>
            </ModalBody>
            {loading && <SpinnerLoad />}
            <ModalFooter className="bg-logoblue">
              <Button mr={3} type="submit" isLoading={loading}>
                {isUpdate ? "Update" : "Save"}
              </Button>
              <Button onClick={handleModalClose} isDisabled={loading}>
                Cancel
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </div>
  )
}

export default JobPreferences2